// SF-4956: start downloading the survey background as soon as the survey data arrives
// (Login / Welcome), so it is already in the browser cache when the questions open.
// BackgroundSecond uses the same record to know whether it can show the image right away.

const loadedUrls = new Set();
const pendingLoads = new Map();

/** True when this exact image URL has already finished downloading in this page. */
export function isSurveyBackgroundLoaded(url) {
  return Boolean(url) && loadedUrls.has(url);
}

/**
 * Download the image once per URL. Resolves true when it loaded, false when there is no URL
 * or it failed (expired link, deleted file), so callers can fall back to the plain background.
 * @param {string|null|undefined} url
 * @returns {Promise<boolean>}
 */
export function preloadSurveyBackground(url) {
  if (!url || typeof Image === "undefined") return Promise.resolve(false);
  if (loadedUrls.has(url)) return Promise.resolve(true);
  if (pendingLoads.has(url)) return pendingLoads.get(url);

  const load = new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      loadedUrls.add(url);
      pendingLoads.delete(url);
      resolve(true);
    };
    image.onerror = () => {
      pendingLoads.delete(url);
      resolve(false);
    };
    image.src = url;
  });
  pendingLoads.set(url, load);
  return load;
}

/** Test helper: forget every URL. */
export function resetSurveyBackgroundCache() {
  loadedUrls.clear();
  pendingLoads.clear();
}
